import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatAge } from "./supportUtils";
import { useClientTicketHistory } from "./useClientTicketHistory";
import { getDisplayId, getRoomId, isMerged, isResolved } from "./ticketUtils";

/** One selectable ticket card. */
const TicketCard = ({ ticket, selected, name, onSelect, hint }) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    className={`w-full rounded-md border px-3 py-2 text-left transition-colors ${
      selected
        ? "border-orange-primary bg-light-orange"
        : "border-gray-200 bg-white hover:bg-gray-50"
    }`}
  >
    <p className="m-0 flex items-baseline gap-1.5">
      <span className="font-mono text-xs font-bold text-gray-500">
        #{getDisplayId(ticket)}
      </span>
      <span className="min-w-0 flex-1 truncate text-xs font-semibold text-gray-900">
        {ticket.subject || "(no subject)"}
      </span>
    </p>
    <p className="m-0 mt-0.5 text-[11px] text-gray-500">
      {name}
      {ticket.created_at ? ` · ${formatAge(ticket.created_at)}` : ""}
      {hint ? ` · ${hint}` : ""}
    </p>
  </button>
);

/**
 * Merge two tickets — pick the duplicate, then pick which one survives.
 *
 * Two things the wording has to get right, because both are irreversible and
 * both are commonly misremembered:
 *
 *  - **Messages do not move.** v1.9 merge is link-only: the loser keeps its own
 *    messages, points at the winner, stops its clocks and archives. Moving
 *    messages would break the per-room `seq` that every pagination cursor and
 *    read receipt depends on. (An older spec draft said otherwise.)
 *  - **Direction matters.** Merging A into B is not the same as B into A — the
 *    survivor keeps its SLA history, so choosing wrongly can bury a breach.
 *
 * @param {Object} props
 * @param {Object} props.ticket - The currently open ticket.
 * @param {string|null} props.clientId - Requester, for candidate tickets.
 * @param {string} props.clientName
 * @param {() => void} props.onClose
 * @param {(payload: { sourceRoomId: string, target_room_id: string,
 *   notify_customer: boolean }) => Promise<void>} props.onMerge - `sourceRoomId`
 *   is the ticket being merged away, which is not always the open one.
 */
export default function MergeTicketDialog({
  ticket,
  clientId,
  clientName,
  onClose,
  onMerge,
}) {
  const { tickets: history, loading } = useClientTicketHistory(clientId);
  const [otherRoomId, setOtherRoomId] = useState(null);
  const [primaryRoomId, setPrimaryRoomId] = useState(null);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const ref = useRef(null);

  const currentRoomId = getRoomId(ticket);

  // Candidates: the requester's other live tickets. A resolved or
  // already-merged ticket can't be merged again, so it isn't offered.
  const candidates = useMemo(
    () =>
      history.filter(
        (t) =>
          String(getRoomId(t)) !== String(currentRoomId) &&
          !isMerged(t) &&
          !isResolved(t)
      ),
    [history, currentRoomId]
  );

  useEffect(() => {
    const onOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onEscape = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEscape);
      document.body.style.overflow = "auto";
    };
  }, [onClose]);

  const other = candidates.find((t) => String(getRoomId(t)) === String(otherRoomId));
  const primaryIsCurrent = String(primaryRoomId) === String(currentRoomId);
  const loser = primaryIsCurrent ? other : ticket;
  const winner = primaryIsCurrent ? ticket : other;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!other || !primaryRoomId) {
      setErrorMessage("Pick the duplicate and which ticket survives.");
      return;
    }
    setSubmitting(true);
    setErrorMessage("");
    try {
      await onMerge({
        sourceRoomId: String(getRoomId(loser)),
        target_room_id: String(getRoomId(winner)),
        notify_customer: notifyCustomer,
      });
      onClose();
    } catch (err) {
      setSubmitting(false);
      setErrorMessage(err?.message || "That merge didn't go through.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Merge tickets"
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
      >
        <h2 className="m-0 text-base font-bold text-gray-800">Merge tickets</h2>
        <p className="m-0 mt-1 text-xs text-gray-500">
          Two tickets for the same problem. One survives; the other closes and points
          at it.
        </p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
              Which ticket is the duplicate of this one?
            </p>
            {loading ? (
              <p className="m-0 text-xs text-gray-400">Loading their other tickets…</p>
            ) : candidates.length === 0 ? (
              <p className="m-0 text-xs text-gray-500">
                {clientName} has no other open tickets to merge with.
              </p>
            ) : (
              <div className="space-y-1.5">
                {candidates.map((t) => (
                  <TicketCard
                    key={getRoomId(t)}
                    ticket={t}
                    name={clientName}
                    selected={String(getRoomId(t)) === String(otherRoomId)}
                    onSelect={() => {
                      setOtherRoomId(getRoomId(t));
                      setPrimaryRoomId(null);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {other && (
            <div>
              <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
                Keep as the primary ticket
              </p>
              <div className="space-y-1.5">
                <TicketCard
                  ticket={ticket}
                  name={clientName}
                  hint="the one you're looking at"
                  selected={primaryIsCurrent}
                  onSelect={() => setPrimaryRoomId(currentRoomId)}
                />
                <TicketCard
                  ticket={other}
                  name={clientName}
                  selected={String(primaryRoomId) === String(getRoomId(other))}
                  onSelect={() => setPrimaryRoomId(getRoomId(other))}
                />
              </div>
            </div>
          )}

          {winner && loser && (
            <p className="m-0 rounded-md bg-gray-100 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
              <span className="font-mono font-bold">#{getDisplayId(loser)}</span> closes as
              merged and links to{" "}
              <span className="font-mono font-bold">#{getDisplayId(winner)}</span>. Its
              SLA clocks stop and its room is archived.{" "}
              <strong>Its messages stay where they are</strong> — the customer keeps both
              threads, they just stop being two open tickets. This can't be undone.
            </p>
          )}

          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={notifyCustomer}
              onChange={(e) => setNotifyCustomer(e.target.checked)}
              className="h-4 w-4 accent-orange-primary"
            />
            Tell the customer we merged the threads
          </label>

          {errorMessage && (
            <p className="m-0 text-xs font-semibold text-danger-dark">{errorMessage}</p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !other || !primaryRoomId}
              className="rounded-lg bg-orange-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting
                ? "Merging…"
                : winner
                ? `Merge into #${getDisplayId(winner)}`
                : "Merge"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
